import { APIModel, DisplayableType } from "../api-model";
import { makeGetRequest } from "@/src/utils/api-utils";
// @ts-ignore
import { APIRoute } from "@/src/endpoints";
import { APIPagyResponse } from "@/src/types";
import { AssociatedUser } from "../associated-user";
import beApiClient from "@/src/be-api-client";

export type MaskingMethodValue = string | null;
export type AvailableMaskings = Record<string, string>;

type StoredZipFile = {
  stored_at: string;
  stored_file: {
    original_filename: string;
    file_ext: string;
    file_type: string;
    upload_identifier: string;
    upload_status: string;
  };
};

type Details = {
  stored_zip_file: StoredZipFile | null;
  masked_zip_file: StoredZipFile | null;
  error: string | null;
}

export type FileMaskingConfig = {
  filename: string;
  column_masks: Record<string, MaskingMethodValue>;
  errors: Array<any>;
};

export type MaskingConfiguration = {
  file_masking_config: Array<FileMaskingConfig>;
};

export const STATUS_MAP: Record<string, { text: string; className: string }> = {
  pending:                      { text: 'Pending',                    className: 'bg-secondary text-white' },
  uploaded:                     { text: 'Uploaded',                   className: 'bg-secondary text-white' },
  processing_columns:           { text: 'Processing Columns',         className: 'bg-warning text-dark' },
  pending_masking_config:       { text: 'Pending Masking Config',     className: 'bg-warning text-dark' },
  masking_files:                { text: 'Masking Files',              className: 'bg-warning text-dark' },
  masking_complete:             { text: 'Masking Complete',           className: 'bg-success text-white' },
  masking_complete_with_errors: { text: 'Masking Complete with Errors', className: 'bg-danger text-white' },
};

export class MaskCSVZip extends APIModel {
  id!: string;
  status!: string;

  get statusDisplayText(): string {
    return STATUS_MAP[this.status]?.text ?? this.status;
  }

  get statusBadgeClass(): string {
    return STATUS_MAP[this.status]?.className ?? 'bg-secondary text-white';
  }
  details!: Details;
  uploaded_by_id!: string | null;
  created_by_id!: string | null;
  updated_by_id!: string | null;
  uploaded_by!: AssociatedUser;
  created_by!: AssociatedUser;
  updated_by!: AssociatedUser;
  created_at!: DisplayableType;
  updated_at!: DisplayableType;
  masking_configuration!: MaskingConfiguration;

  static EP = {
    index: '/admin/unos/mask_csv_zips',
    show: '/admin/unos/mask_csv_zips/:id',
    upload: '/admin/unos/mask_csv_zips/:mask_csv_zip_id/upload',
    extractColumnInfo: '/admin/unos/mask_csv_zips/:mask_csv_zip_id/extract_column_info',
    runMasking: '/admin/unos/mask_csv_zips/:mask_csv_zip_id/run_masking',
    downloadMaskedZip: '/admin/unos/mask_csv_zips/:mask_csv_zip_id/download_masked_zip',
    availableMaskings: '/admin/unos/mask_csv_zips/available_maskings',
  }

  static fieldsToMap = {
    direct: [
      'id',
      'status',
      'uploaded_by_id',
      'created_by_id',
      'updated_by_id'
    ],
    formatDateTime: [
      'created_at',
      'updated_at'
    ]
  }

  static async loadByID(id: string): Promise<MaskCSVZip> {
    const url = APIRoute(MaskCSVZip.EP.show, { id });
    const request = await makeGetRequest(url);
    return MaskCSVZip.buildFromAPI(request.data);
  }

  static buildFromAPI(record: any): MaskCSVZip {
    const maskCSVZip: MaskCSVZip = new MaskCSVZip();
    record = record || {};
    MaskCSVZip.mapFieldsDirectly<MaskCSVZip>(MaskCSVZip.fieldsToMap.direct, record, maskCSVZip);
    MaskCSVZip.mapFormatDateTime<MaskCSVZip>(MaskCSVZip.fieldsToMap.formatDateTime, record, maskCSVZip);

    maskCSVZip.details = {
      stored_zip_file: record.details?.stored_zip_file ?? null,
      masked_zip_file: record.details?.masked_zip_file ?? null,
      error: record.details?.error ?? null,
    };

    maskCSVZip.masking_configuration = record.masking_configuration ?? { file_masking_config: [] };

    if (record.uploaded_by) maskCSVZip.uploaded_by = AssociatedUser.buildFromAPI(record.uploaded_by);
    if (record.created_by) maskCSVZip.created_by = AssociatedUser.buildFromAPI(record.created_by);
    if (record.updated_by) maskCSVZip.updated_by = AssociatedUser.buildFromAPI(record.updated_by);

    return maskCSVZip;
  }

  static async search(params: any = {}): Promise<APIPagyResponse<MaskCSVZip>> {
    // axios GET expects query params under `params`; passing the payload flat would drop
    // page/sort_by/sort_type and always return page 1.
    const request = await makeGetRequest(APIRoute(MaskCSVZip.EP.index, {}), { params });
    request.data = request.data.map((v: any) => MaskCSVZip.buildFromAPI(v));
    return request;
  }

  static async create(): Promise<MaskCSVZip> {
    const url = APIRoute(MaskCSVZip.EP.index, {});
    const response = await beApiClient.post(url, {});
    return MaskCSVZip.buildFromAPI(response.data.data);
  }

  static async uploadZip(id: string, file: File): Promise<MaskCSVZip> {
    const url = APIRoute(MaskCSVZip.EP.upload, { mask_csv_zip_id: id });
    const formData = new FormData();
    formData.append('zip_file', file);
    const response = await beApiClient.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return MaskCSVZip.buildFromAPI(response.data.data);
  }

  static async extractColumnInfo(id: string): Promise<MaskCSVZip> {
    const url = APIRoute(MaskCSVZip.EP.extractColumnInfo, { mask_csv_zip_id: id });
    const response = await beApiClient.post(url, {});
    return MaskCSVZip.buildFromAPI(response.data.data);
  }

  static async update(id: string, maskingConfiguration: MaskingConfiguration): Promise<MaskCSVZip> {
    const url = APIRoute(MaskCSVZip.EP.show, { id });
    const response = await beApiClient.patch(url, { masking_configuration: maskingConfiguration });
    return MaskCSVZip.buildFromAPI(response.data.data);
  }

  static async runMasking(id: string): Promise<MaskCSVZip> {
    const url = APIRoute(MaskCSVZip.EP.runMasking, { mask_csv_zip_id: id });
    const response = await beApiClient.post(url, {});
    return MaskCSVZip.buildFromAPI(response.data.data);
  }

  static async loadAvailableMaskings(): Promise<AvailableMaskings> {
    const url = APIRoute(MaskCSVZip.EP.availableMaskings, {});
    const response = await beApiClient.get(url);
    return response.data.data ?? {};
  }

  static downloadMaskedZipUrl(id: string): string {
    return APIRoute(MaskCSVZip.EP.downloadMaskedZip, { mask_csv_zip_id: id });
  }
}
